import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, CircularProgress, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import LifecycleActions from './components/LifecycleActions';
import ScenarioStatus from './components/ScenarioStatus';
import DemandAllocationView from './DemandAllocationView';
import ScheduleTimeline from './ScheduleTimeline';
import BatchDetail from './BatchDetail';
import ReoptimizationReview from './ReoptimizationReview';
import useScenarioDetail from './hooks/useScenarioDetail';
import { mergeScenarioBatches } from './utils/scenarioState';
import {
  approveScenario, cancelScenario, publishScenario, reconcileScenario, solveScenario,
} from './api/tumblerApi';
import normalizeApiError from './utils/normalizeApiError';

// Detail view for one scenario, fetched by id directly (useScenarioDetail) so it survives list
// pagination and always reflects the server's current status. Lifecycle actions are dispatched
// here and their *outcome* — not the HTTP 200 — decides what the UI reports
// (ASSESSMENT.md P1 / IMPLEMENTATION_PLAN.md Phase 1).
export default function ScenarioDetail({ scenarioId, onScenarioChanged, onRevisionCreated }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const { loading, error, scenario, reload } = useScenarioDetail({ scenarioId, accessToken });
  const [busyAction, setBusyAction] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [tab, setTab] = useState('demand');
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  useEffect(() => { setSelectedBatchId(null); }, [scenarioId]);

  const runners = {
    solve: solveScenario,
    approve: approveScenario,
    publish: publishScenario,
    reconcile: reconcileScenario,
    cancel: cancelScenario,
  };

  const handleAction = async (action) => {
    if (action === 'reoptimize') {
      setTab('reoptimize');
      return;
    }
    const runner = runners[action];
    if (!runner) return;
    setBusyAction(action);
    setActionMessage(null);
    try {
      const response = await runner({ scenarioId, accessToken });
      const nextStatus = response.data?.solver_metadata?.status || response.data?.status;
      // Decide the message from the returned resource's own status, never from HTTP success alone.
      const severity = ['INFEASIBLE', 'ERROR'].includes(nextStatus) ? 'error'
        : ['STOCK_ONLY', 'NO_DEMAND'].includes(nextStatus) ? 'warning'
        : 'success';
      setActionMessage({ severity, text: action === 'reconcile' ? `Reconciled: ${JSON.stringify(response.data)}` : `Scenario ${action} finished — server status: ${nextStatus || 'unknown'}.` });
    } catch (error) {
      setActionMessage({ severity: 'error', text: normalizeApiError(error, `Unable to ${action} scenario.`).summary });
    } finally {
      setBusyAction(null);
      await reload();
      onScenarioChanged?.();
    }
  };

  if (!scenarioId) {
    return <Typography color="text.secondary">Select a scenario from the list to review it.</Typography>;
  }

  if (loading && !scenario) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error.summary}</Alert>;
  }

  if (!scenario) return null;

  const batches = mergeScenarioBatches(scenario);
  const selectedBatch = batches.find((b) => b.id === selectedBatchId);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
        <Box>
          <Typography variant="h5">Scenario #{scenario.id}</Typography>
          <Typography color="text.secondary">
            {String(scenario.planning_start).replace('T', ' ').slice(0, 16)} → {String(scenario.planning_end).replace('T', ' ').slice(0, 16)} (Bangkok)
          </Typography>
        </Box>
        <ScenarioStatus status={scenario.status} reason={scenario.status_reason} />
      </Stack>

      {actionMessage && <Alert severity={actionMessage.severity} onClose={() => setActionMessage(null)} sx={{ mt: 2 }}>{actionMessage.text}</Alert>}

      {['INFEASIBLE', 'ERROR'].includes(scenario.status) && scenario.status_reason && (
        <Alert severity="error" sx={{ mt: 2 }}>{scenario.status_reason}</Alert>
      )}
      {scenario.solver_metadata?.status === 'STOCK_ONLY' && (
        <Alert severity="warning" sx={{ mt: 2 }}>This plan only uses existing in-stock tumbler records — no fresh demand was scheduled.</Alert>
      )}
      {scenario.solver_metadata?.status === 'NO_DEMAND' && (
        <Alert severity="warning" sx={{ mt: 2 }}>No demand found for this planning window.</Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <LifecycleActions status={scenario.status} busyAction={busyAction} onAction={handleAction} />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="demand" label="Demand & FEFO" />
        <Tab value="timeline" label="Schedule" />
        <Tab value="batch" label="Batch detail" disabled={!selectedBatch} />
        <Tab value="reoptimize" label="Re-optimize" />
      </Tabs>

      {tab === 'demand' && <DemandAllocationView scenario={scenario} />}
      {tab === 'timeline' && (
        <ScheduleTimeline
          scenario={scenario}
          setupActivities={scenario.setup_activities || []}
        />
      )}
      {tab === 'batch' && (
        <BatchDetail key={selectedBatch?.id} batch={selectedBatch} onChanged={async () => { await reload(); onScenarioChanged?.(); }} />
      )}
      {tab === 'reoptimize' && (
        <ReoptimizationReview
          scenario={scenario}
          onRevisionCreated={onRevisionCreated}
          onRefetchPublished={reload}
        />
      )}

      {tab === 'timeline' && batches.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          {batches.map((batch) => (
            <Box
              key={batch.id}
              onClick={() => { setSelectedBatchId(batch.id); setTab('batch'); }}
              sx={{ cursor: 'pointer', fontSize: 12, px: 1, py: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            >
              Open batch #{batch.id}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
